import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { HttpErrorResponse } from '@angular/common/http';

import { ModuleConfiguration } from '../vocabulary.module.config';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import { CodeSystemVersionListModel } from '../models/CodeSystemVersionListModel';
import { CodeSystemVersionDashboardModel } from '../models/CodeSystemVersionDashboardModel';
import { ConceptWorkspaceModel } from '../models/ConceptWorkspaceModel';
import { ValueSetModel } from '../models/ValueSetModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ResponseMessage } from '../../core/services/ResponseMessage';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { VocabularyService } from './VocabularyService';
import * as vocabularyActions from '../store/actions';
import { LoadPagePayloadWithCustomParams } from '../store/actions/LoadPagePayloadWithCustomParams';
import { TermModel } from '../models/TermModel';
import { PropertyModel } from "../models/PropertyModel";
import { RelationshipModel } from "../models/RelationshipModel";
import { AddConceptVersionModel} from "../models/AddConceptVersionModel";
import { DeleteConceptVersionModel } from '../models/DeleteConceptVersionModel';
import { UndoDeleteConceptVersionModel } from '../models/UndoDeleteConceptVersionModel';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { AddCodeSystemModel } from '../models/AddCodeSystemModel';
import { AddValueSetModel } from '../models/AddValueSetModel';
import { RelateConceptVersionModel} from "../models/RelateConceptVersionModel";


@Injectable()
export class VocabularyServiceEffects {
  onlyOnce = true;
  @Effect()
  getCodeSystemVersionList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<CodeSystemVersionListModel>(ModuleConfiguration.moduleName, CodeSystemVersionListModel))
      .map((a: dashboardActions.QueryListAction<CodeSystemVersionListModel>) => a.payload)
    .switchMap((payload: LoadPagePayload) => this.vocabularyService.getCodeSystemVersionList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm, payload.loadLatest )
      .map((response: QueryResponse<CodeSystemVersionListModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, CodeSystemVersionListModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getValueSets$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<CodeSystemVersionDashboardModel>(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel))
      .map((a: vocabularyActions.GetValueSetListAction<CodeSystemVersionDashboardModel>) => a.payload)
      .switchMap((payload: { key: string, searchTerm: string, hideEmpty: boolean }) => this.vocabularyService.getValueSets(payload.key, payload.searchTerm, payload.hideEmpty)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<CodeSystemVersionDashboardModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<CodeSystemVersionDashboardModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { model: response.model });
      }));
  @Effect()
  getConceptsForValueSet$: Observable<Action> = this.actions$.ofType(vocabularyActions.getConceptListType<ValueSetModel>(ModuleConfiguration.moduleName, ValueSetModel))
      .map((a: vocabularyActions.GetConceptListAction<ValueSetModel>) => a.payload)
      .switchMap((payload: LoadPagePayloadWithCustomParams) => this.vocabularyService.getConceptsForValueSet(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.key, payload.version, payload.searchTerm)
        .map((response: QueryResponse<ValueSetModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, ValueSetModel, { errors: response.messages.map(m => m.value) });
        }
          return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, ValueSetModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getSelectedConcept$: Observable<Action> = this.actions$.ofType(vocabularyActions.getSelctedConceptType<ConceptWorkspaceModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel))
      .map((a: vocabularyActions.GetSelctedConceptAction<ConceptWorkspaceModel>) => a.payload)
      .switchMap((payload: { key: string, code: string }) => this.vocabularyService.getSelectedConcept(payload.key, payload.code)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<ConceptWorkspaceModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<ConceptWorkspaceModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, ConceptWorkspaceModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, ConceptWorkspaceModel, { model: response.model });
      }));

  @Effect()
  addTerm$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<ConceptWorkspaceModel, TermModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, TermModel))
      .filter((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, TermModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
      .switchMap((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, TermModel>) => this.vocabularyService.addTerm(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<TermModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
        .map((response: ModelResponse<TermModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel, TermModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseTerm$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<ConceptWorkspaceModel, TermModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, TermModel))
      .filter((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, TermModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
      .switchMap((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, TermModel>) => this.vocabularyService.reviseTerm(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<TermModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
        .map((response: ModelResponse<TermModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel,
          TermModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeTerm$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<ConceptWorkspaceModel, TermModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, TermModel))
      .switchMap((a: workspaceActions.RemoveModelAction<ConceptWorkspaceModel, TermModel>) => this.vocabularyService.removeTerm(a.payload.key, a.payload.model.termKey)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<TermModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
        .map((response: ModelResponse<TermModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel,
          TermModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));

  @Effect()
  addProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<ConceptWorkspaceModel, PropertyModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, PropertyModel))
    .filter((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, PropertyModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, PropertyModel>) => this.vocabularyService.addProperty(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PropertyModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel, PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<ConceptWorkspaceModel, PropertyModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, PropertyModel))
    .filter((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, PropertyModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
    .switchMap((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, PropertyModel>) => this.vocabularyService.reviseProperty(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PropertyModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel,
          PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<ConceptWorkspaceModel, PropertyModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, PropertyModel))
    .switchMap((a: workspaceActions.RemoveModelAction<ConceptWorkspaceModel, PropertyModel>) => this.vocabularyService.removeProperty(a.payload.key, a.payload.model.key)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<PropertyModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel,
          PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addConcept$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddConceptVersionModel>(ModuleConfiguration.moduleName, AddConceptVersionModel))
      .switchMap((a: dashboardActions.AddAction<AddConceptVersionModel>) => this.vocabularyService.addConcept(a.payload.model)
        .map((response: ModelResponse<AddConceptVersionModel>) => {
        if (response.hasError) {          
          return new ActionError(a, response.messages.map(m => m.value));
          }
        var hasWarning = false;
        if (response.messages.length > 0) {
          for (var i = 0; i < response.messages.length; i++) {
            if (response.messages[i].type === ResponseMessageType.Warning) {
              hasWarning = true;
              break;
            }
          }          
        }
        if (hasWarning) {
          return new vocabularyActions.DuplicationFoundAction({ model: response.model });
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddConceptVersionModel, { key: response.model.code });
      }));
  @Effect()
  addCodeSystemVersionDraft$: Observable<Action> = this.actions$.ofType(vocabularyActions.CREATE_CODESYSTEMVERSION_DRAFT)
      .map((a: vocabularyActions.CreateCodeSystemVersionDraftAction) => a.payload)
      .switchMap((payload: { key: string }) => this.vocabularyService.createDraft(payload.key)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { errors: response.messages.map(m => m.value) });
        }
        return new vocabularyActions.CreateCodeSystemVersionDraftSuccessAction( { key: response.model });
      }));
  @Effect()
  finalizeDraft$: Observable<Action> = this.actions$.ofType(vocabularyActions.FINALIZE_CODESYSTEMVERSION_DRAFT)
    .map((a: vocabularyActions.FinalizeCodeSystemVersionDraftAction) => a.payload)
      .switchMap((payload: { key: string }) => this.vocabularyService.finalizeDraft(payload.key)
        .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { errors: response.messages.map(m => m.value) });
        }
        return new vocabularyActions.FinalizeCodeSystemVersionDraftSuccessAction({ success: response.model });
      }));
  @Effect()
  clearCache$: Observable<Action> = this.actions$.ofType(vocabularyActions.CLEAR_VOCABULARY_CACHE)
      .switchMap((a: vocabularyActions.ClearVocabularyCacheAction) => this.vocabularyService.clearCache()
        .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { errors: response.messages.map(m => m.value) });
        }
        return new vocabularyActions.ClearVocabularyCacheSuccessAction({ success: response.model });
      }));
  @Effect()
  addRelationship$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<ConceptWorkspaceModel, RelationshipModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, RelationshipModel))
    .filter((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, RelationshipModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<ConceptWorkspaceModel, RelationshipModel>) => this.vocabularyService.addRelationship(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<RelationshipModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<RelationshipModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel, RelationshipModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeRelationship$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<ConceptWorkspaceModel, RelationshipModel>(ModuleConfiguration.moduleName, ConceptWorkspaceModel, RelationshipModel))
      .switchMap((a: workspaceActions.RemoveModelAction<ConceptWorkspaceModel, RelationshipModel>) => this.vocabularyService.removeRelationship(a.payload.key, a.payload.model.relationshipKey)
      .catch((error: HttpErrorResponse) => {
        var response = new ModelResponse<RelationshipModel>();
        response.hasError = true;
        var responseMessage = new ResponseMessage();
        responseMessage.type = ResponseMessageType.Error;
        responseMessage.value = error.message;
        response.messages = [responseMessage];
        return Observable.of(response);
      })
      .map((response: ModelResponse<RelationshipModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          ConceptWorkspaceModel,
          RelationshipModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  deleteDraft$: Observable<Action> = this.actions$.ofType(vocabularyActions.DELETE_CODESYSTEMVERSION_DRAFT)
    .map((a: vocabularyActions.DeleteCodeSystemVersionDraftAction) => a.payload)
    .switchMap((payload: { key: string }) => this.vocabularyService.deleteDraft(payload.key)
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, CodeSystemVersionDashboardModel, { errors: response.messages.map(m => m.value) });
        }
        return new vocabularyActions.DeleteCodeSystemVersionDraftSuccessAction({ success: response.model });
      }));
  @Effect()
  deleteConcept$: Observable<Action> = this.actions$.ofType(dashboardActions.deleteType<DeleteConceptVersionModel>(ModuleConfiguration.moduleName, DeleteConceptVersionModel))
      .switchMap((a: dashboardActions.DeleteAction<DeleteConceptVersionModel>) => this.vocabularyService.deleteConcept(a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.DeleteSuccessAction(ModuleConfiguration.moduleName, DeleteConceptVersionModel, { key: response.model });
      }));
  @Effect()
  undoDeleteConcept$: Observable<Action> = this.actions$.ofType(vocabularyActions.UNDO_DELETE_CONCEPT_VERSION)
      .switchMap((a: vocabularyActions.UndoDeleteConceptVersionAction) => this.vocabularyService.undoDeleteConcept(a.payload.model)
        .map((response: ModelResponse<UndoDeleteConceptVersionModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
          return new vocabularyActions.UndoDeleteConceptVersionSuccessAction({ restoredConcept: response.model });
      }));
  @Effect()
  addCodeSystem$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddCodeSystemModel>(ModuleConfiguration.moduleName, AddCodeSystemModel))
    .switchMap((a: dashboardActions.AddAction<AddCodeSystemModel>) => this.vocabularyService.addCodeSystem(a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddCodeSystemModel, { key: response.model });
      }));
  @Effect()
  addValueSet$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddValueSetModel>(ModuleConfiguration.moduleName, AddValueSetModel))
    .switchMap((a: dashboardActions.AddAction<AddValueSetModel>) => this.vocabularyService.addValueSet (a.payload.model)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddValueSetModel, { key: response.model });
      }));
  @Effect()
  relateConceptToValueSet$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<RelateConceptVersionModel>(ModuleConfiguration.moduleName, RelateConceptVersionModel))
      .switchMap((a: dashboardActions.AddAction<RelateConceptVersionModel>) => this.vocabularyService.relateConceptToValueSet(a.payload.model)
        .map((response: ModelResponse<RelateConceptVersionModel>) => {
        if (response.hasError) {          
          return new ActionError(a, response.messages.map(m => m.value));
          }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, RelateConceptVersionModel, { key: response.model.code });
      }));

  constructor(
    private vocabularyService: VocabularyService,
    private actions$: Actions
  ) {
  }
}



// WEBPACK FOOTER //
// ./src/app/vocabulary/services/VocabularyServiceEffects.ts